// InvoicesPanel.tsx
// Displays invoice history fetched from Stripe

import { useState } from 'react';
import { VSpace } from '../../account/account-settings';
import { MutedText, Title } from '../../account/account-settings.styled';
import { Panel } from '../../settings.styled';
import { ActionText } from '../../account/account-settings.styled';
import { MetaRow, Paid, PastDue, DownloadLink, Divider } from '../billing.styled';
import { useSettingsContext } from '../../settings-context';
// import { paymentsApi } from '../../../../../api/payments/payments'; // Stripe disabled
// import type { StripeInvoice } from '../../../../../api/payments/payments.types'; // Stripe disabled

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatCents(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function InvoicesPanel() {
  const { planState, paymentInfo } = useSettingsContext();

  const [invoices, _setInvoices] = useState<any[]>([]); // StripeInvoice[] — Stripe disabled
  const [loading, _setLoading] = useState(false);
  const [error, _setError] = useState<string | null>(null);

  const hasStripeCustomer = !!paymentInfo?.stripe_customer_id;
  const showInvoices = planState !== 'trial' && hasStripeCustomer;

  // useEffect(() => {
  //   if (!showInvoices) return;
  //   let cancelled = false;
  //   setLoading(true);
  //   setError(null);
  //   paymentsApi
  //     .getInvoices()
  //     .then(({ invoices: data }) => {
  //       if (!cancelled) setInvoices(data);
  //     })
  //     .catch(() => {
  //       if (!cancelled) setError('Failed to load invoices.');
  //     })
  //     .finally(() => {
  //       if (!cancelled) setLoading(false);
  //     });
  //   return () => { cancelled = true; };
  // }, [showInvoices]);

  if (!showInvoices) {
    return (
      <Panel>
        <Title>Invoices</Title>
        <VSpace $h={8} />
        <MutedText>
          No invoices yet. You'll see your first invoice after your first payment.
        </MutedText>
      </Panel>
    );
  }

  return (
    <Panel>
      <Title>Invoices</Title>
      <VSpace $h={8} />

      {loading && <MutedText>Loading invoices...</MutedText>}
      {error && (
        <>
          <MutedText style={{ color: '#d65f46' }}>{error}</MutedText>
          <VSpace $h={8} />
          <ActionText
            style={{ fontSize: '13px', cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onClick={() => {
              // DISABLED: Stripe invoice fetching — payments handled by private invoices
              // setError(null);
              // setLoading(true);
              // paymentsApi
              //   .getInvoices()
              //   .then(({ invoices: data }) => setInvoices(data))
              //   .catch(() => setError('Failed to load invoices.'))
              //   .finally(() => setLoading(false));
            }}
          >
            Retry
          </ActionText>
        </>
      )}

      {!loading && !error && invoices.length === 0 && <MutedText>No invoices found.</MutedText>}

      {!loading &&
        !error &&
        invoices.map((inv, idx) => (
          <div key={inv.id}>
            {idx > 0 && <Divider />}
            <VSpace $h={idx > 0 ? 10 : 0} />
            <MetaRow>
              <div>
                <Title style={{ fontSize: '13px' }}>
                  {formatCents(inv.amount_paid, inv.currency)}
                </Title>
                <MutedText style={{ fontSize: '11px' }}>
                  {formatDate(inv.created)}
                  {inv.number ? ` · ${inv.number}` : ''}
                </MutedText>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {inv.status === 'paid' ? <Paid>Paid</Paid> : <PastDue>{inv.status}</PastDue>}
                {inv.invoice_pdf && (
                  <a
                    href={inv.invoice_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <DownloadLink as="span">PDF</DownloadLink>
                  </a>
                )}
              </div>
            </MetaRow>
            <VSpace $h={10} />
          </div>
        ))}

      {!loading && !error && invoices.length > 0 && (
        <>
          <VSpace $h={4} />
          <ActionText
            style={{ fontSize: '13px', cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onClick={async () => {
              // DISABLED: Stripe billing portal — payments handled by private invoices
              // try {
              //   const { url } = await paymentsApi.createPortalSession();
              //   window.open(url, '_blank');
              // } catch {
              //   setError('Failed to open billing portal. Please try again.');
              // }
            }}
          >
            View all in billing portal
          </ActionText>
        </>
      )}
    </Panel>
  );
}
